import React from 'react';
import { useTranslation } from 'react-i18next';
import { LeaderboardEntry } from '../types';
import { Card, CardContent } from '../../../components/ui/card';
import { Trophy } from 'lucide-react';

interface LeaderboardTopProps {
    entries: LeaderboardEntry[];
}

export const LeaderboardTop: React.FC<LeaderboardTopProps> = ({ entries }) => {
    const { t } = useTranslation('common');

    // Reorder for visual display: 2nd, 1st, 3rd
    const displayOrder = [1, 0, 2];
    const topEntries = displayOrder.map(idx => entries[idx]).filter(Boolean);

    return (
        <div className="flex flex-col md:flex-row justify-center items-end gap-4 mb-12">
            {topEntries.map((entry) => {
                const isFirst = entry.rank === 1;
                const heightClass = isFirst ? 'md:h-64' : 'md:h-56';
                const colorClass =
                    entry.rank === 1 ? 'bg-yellow-100 border-yellow-200 text-yellow-700' :
                        entry.rank === 2 ? 'bg-gray-100 border-gray-200 text-gray-700' :
                            'bg-orange-100 border-orange-200 text-orange-800';

                return (
                    <Card
                        key={entry.id}
                        className={`w-full md:w-64 relative border-2 ${colorClass} ${heightClass} flex flex-col items-center justify-end pb-6 pt-12 transition-transform hover:-translate-y-1`}
                    >
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className={`
                                w-20 h-20 rounded-full border-4 shadow-md bg-white overflow-hidden flex items-center justify-center
                                ${entry.rank === 1 ? 'border-yellow-400' : entry.rank === 2 ? 'border-gray-400' : 'border-orange-400'}
                             `}>
                                {entry.avatar ? (
                                    <img src={entry.avatar} alt={entry.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-2xl font-bold text-gray-400">{entry.name[0]}</span>
                                )}
                            </div>
                            <div className={`
                                absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm
                                ${entry.rank === 1 ? 'bg-yellow-500' : entry.rank === 2 ? 'bg-gray-500' : 'bg-orange-500'}
                             `}>
                                {entry.rank}
                            </div>
                        </div>

                        <div className="text-center px-4">
                            <h3 className="font-bold text-lg mb-1 line-clamp-1 text-gray-900">{entry.name}</h3>
                            <div className="font-bold text-2xl mb-2">
                                {entry.points.toLocaleString()} <span className="text-xs font-normal opacity-70">pts</span>
                            </div>
                            <div className="text-sm opacity-80 font-medium">
                                {t('leaderboard.card.courses', { count: entry.coursesCompleted })}
                            </div>
                        </div>

                        {isFirst && (
                            <Trophy className="absolute -top-12 text-yellow-500 animate-bounce" size={40} />
                        )}
                    </Card>
                );
            })}
        </div>
    );
};
